/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { AbstractControl, AsyncValidatorFn, ValidatorFn } from '@angular/forms';
import {
  alwaysTrue,
  AppControl,
  ApplicationState,
  ApplicationStateHolder,
  ApplicationStatePredicate,
  BaseControlSchema,
  BaseControlSchemaConfig, ControlSchemaPackedValue,
  ControlSchemaType,
  ControlSchemaWhenPrivileged,
  whenAllOf,
  whenHasOneOfPrivilegedTokens,
  whenOneOf,
} from '@gh/common-shared';
import { requiredFormValidator } from '@gh/core-ui';
import { coalesce } from '@gh/core-util';
import { isArray, isNil, negate } from 'lodash';
import { whenOfficeUser } from '../app-state/builder-application-state-util';

export abstract class AbstractBuilderControlSchema implements BaseControlSchema {
  abstract schemaType: ControlSchemaType;
  key?: string;
  visible?: ApplicationStatePredicate;
  required?: ApplicationStatePredicate;
  enabled?: ApplicationStatePredicate;
  privilegedEntryPoint?: string[];
  whenPrivileged: ControlSchemaWhenPrivileged;
  validators: ValidatorFn[] = [];
  asyncValidators: AsyncValidatorFn[] = [];
  private partlyVisible?: ApplicationStatePredicate;
  private partlyEnabled?: ApplicationStatePredicate;
  private partlyRequired?: ApplicationStatePredicate;
  private _stateHolder: ApplicationStateHolder;
  private _firstUpdate: boolean = true;

  constructor(stateHolder: ApplicationStateHolder) {
    this._stateHolder = stateHolder;
  }

  get state(): ApplicationState {
    return this._stateHolder.state;
  }

  get stateHolder(): ApplicationStateHolder {
    return this._stateHolder;
  }

  update(config: BaseControlSchemaConfig): void {
    if (!this.isFirstUpdate && config.key !== this.key) {
      throw new Error(`BuilderFormControlSchema: key cannot be changed, old: ${this.key}, new: ${config.key}`);
    }

    this.key = config.key;

    if (this._firstUpdate) {
      this._firstUpdate = false;
    }

    this.recalculate(config);
  }

  applyTo(control: AppControl, packedValue?: ControlSchemaPackedValue): void {
    const visible = this.isVisible();
    const required = this.isRequired();
    const enabled = this.isEnabled();
    const { validators, asyncValidators } = this;
    let validatorsHasBeenUpdated = false;

    if (control.visible !== visible) {
      control.visible = visible;
    }
    if (control.required !== required || control.lastValidators !== validators || this.isUpdateValidators(control)) {
      control.required = required;
      control.lastValidators = validators;
      control.setValidators(this.buildValidators());
      validatorsHasBeenUpdated = true;
    }
    if (control.lastAsyncValidators !== asyncValidators) {
      control.lastAsyncValidators = asyncValidators;
      control.setAsyncValidators(asyncValidators);
      validatorsHasBeenUpdated = true;
    }
    if (validatorsHasBeenUpdated) {
      control.updateValueAndValidity();
    }
    if (control.enabled !== enabled) {
      if (enabled) {
        control.enable();
      } else {
        control.disable();
      }
    }
    control.privilegedEntryPoint = this.privilegedEntryPoint;
  }

  abstract build(value?: any): AbstractControl;

  protected isUpdateValidators(control: AppControl): boolean {
    return false;
  }

  protected buildValidators(): ValidatorFn[] {
    const required = isNil(this.required) ? false : this.required(this.state);
    return this.validators.concat(required ? [requiredFormValidator] : []);
  }

  protected isEnabled(): boolean {
    return isNil(this.enabled) ? true : this.enabled(this.state);
  }

  protected isVisible(): boolean {
    return isNil(this.visible) ? true : this.visible(this.state);
  }

  protected isRequired(): boolean {
    return isNil(this.required) ? false : this.required(this.state);
  }

  protected isPrivileged(): boolean {
    return !isNil(this.privilegedEntryPoint);
  }

  protected isFirstUpdate(): boolean {
    return this._firstUpdate;
  }

  private recalculate(config: BaseControlSchemaConfig): void {
    const privileged = isArray(config.privileged) ?
      (config.privileged.length > 0 ? config.privileged : void 0)
      : (config.privileged ? [config.privileged] : void 0);
    const isVisible = () => this.isVisible();

    this.partlyVisible = isNil(config.visible) ? this.visible : whenAllOf(config.visible);
    this.partlyRequired = isNil(config.required) ? this.partlyRequired : whenAllOf(config.required);
    this.required = isNil(this.partlyRequired) ? this.required : whenAllOf(isVisible, this.partlyRequired);
    this.partlyEnabled = isNil(config.enabled) ? this.partlyEnabled : whenAllOf(config.enabled);
    this.privilegedEntryPoint = coalesce(privileged, this.privilegedEntryPoint);
    this.whenPrivileged = config.whenPrivileged || 'disable';

    const isUsePrivileged = whenOneOf(
      () => !this.isPrivileged(),
      negate(whenOfficeUser()),
      this.privilegedEntryPoint ? whenHasOneOfPrivilegedTokens(this.privilegedEntryPoint) : alwaysTrue);

    this.visible = this.whenPrivileged === 'hide'
      ? isNil(this.partlyVisible) ? isUsePrivileged : whenAllOf(isUsePrivileged, this.partlyVisible)
      : this.partlyVisible;

    this.enabled = this.whenPrivileged === 'disable'
      ? isNil(this.partlyEnabled) ? isUsePrivileged : whenAllOf(isUsePrivileged, this.partlyEnabled)
      : this.partlyEnabled;

    this.validators = isNil(config.validators) ? this.validators : config.validators;
    this.asyncValidators = isNil(config.asyncValidators) ? this.asyncValidators : config.asyncValidators;
  }
}
